import z from 'zod'
import { Position } from 'src/common-services'
import { consumerSpendRadiusDataSchema } from 'src/common-services/types/consumer-spend-types'
import { demoRadiiDataSchema } from 'src/common-services/types/demo-types'
import { SimpleVenueWithDistance } from 'src/common-services/types/foursquare-types'
import { MultiPolygon } from 'src/common-services/types/geojson-types'
import { Day, GetDriveTimePolygonsRequest } from 'src/common-services/types/drive-time-types'
import { NearVenue } from 'src/common-services/types/venues-types'
import {
  BuildoutDriveTimeTypes,
  MAX_DRIVE_TIME_DURATION,
  MAX_RADIUS_RINGS_RADIUS,
} from 'src/common-services/types/partners-type'

export type TradeAreaDate = {
  year: number
  month: number
  day: number
}

export type TradeAreaDates = {
  start: Date
  end: Date
}

export type TradeAreaDateRange = {
  start: TradeAreaDate
  end: TradeAreaDate
}

export enum TradeAreaType {
  HOME = 'HOME',
  WORK = 'WORK',
  BOTH = 'BOTH',
}

export const tradeAreaItemSchema = z.object({
  h3: z.string(),
  count: z.number(),
})
export type TradeAreaItem = z.infer<typeof tradeAreaItemSchema>

export type TradeAreaRequest = {
  venueId: string
  dateRange: TradeAreaDateRange
  type: TradeAreaType
}

export type TradeAreaResponse = {
  request: TradeAreaRequest
  tradeArea: TradeAreaItem[]
}

export type BuildoutTradeAreaResponseMeta = {
  mapCenter: Position
}

export type TradeAreaDateRangeResponse = TradeAreaDateRange[]

type CommonTradeAreaDetailsRequest = {
  venueId: string
  dateRange: TradeAreaDateRange
  center: Position
  naicsCode: string
}

export const buildoutTradeAreaDetailsOverride = z.object({
  driveTime: z.object({
    day: z.nativeEnum(Day),
    type: z.nativeEnum(BuildoutDriveTimeTypes),
    durations: z.array(z.number().int().min(1).max(MAX_DRIVE_TIME_DURATION)),
  }),
  radiusRings: z.object({
    radii: z.array(z.number().gt(0).max(MAX_RADIUS_RINGS_RADIUS)).optional(),
  }),
})

export type BuildoutTradeAreaDetailsOverride = z.infer<typeof buildoutTradeAreaDetailsOverride>

export type BuildoutTradeAreaDetailsRequest = {
  token: string
  override?: string
}

export type FootfallTradeAreaDetailsRequest = CommonTradeAreaDetailsRequest & {
  type: TradeAreaType
  percentages: number[]
}
export type FootfallTradeAreaMapRequest = Omit<FootfallTradeAreaDetailsRequest, 'naicsCode'>

export type DriveTimeTradeAreaDetailsRequest = CommonTradeAreaDetailsRequest & GetDriveTimePolygonsRequest
export type DriveTimeTradeAreaMapRequest = Omit<DriveTimeTradeAreaDetailsRequest, 'naicsCode'>

export type RadiusRingsTradeAreaDetailsRequest = CommonTradeAreaDetailsRequest & {
  radii: number[]
}

export const commonTradeAreaSectionSchema = z.object({
  demographics: demoRadiiDataSchema,
  consumerSpend: consumerSpendRadiusDataSchema,
  nearbyLocationCount: z.number().optional(),
})
export type CommonTradeAreaSection = z.infer<typeof commonTradeAreaSectionSchema>

export type DriveTimeSectionDetails = {
  driveTime: number
  polygon: MultiPolygon
}

export type DriveTimeTradeAreaDetailsResponse = {
  sections: CommonTradeAreaSection[]
} & Partial<BuildoutTradeAreaResponseMeta>

export type RadiiSection = CommonTradeAreaSection & {
  radius: number
}

export type RadiusRingsTradeAreaDetailsResponse = {
  sections: RadiiSection[]
} & Partial<BuildoutTradeAreaResponseMeta>

export const footfallSectionDetailsSchema = z.object({
  percentage: z.number(),
  maxVisitCount: z.number(),
  minVisitCount: z.number(),
  visitsInPercentile: z.number(),
  items: z.array(
    tradeAreaItemSchema.merge(
      z.object({
        distanceMiles: z.number(),
      })
    )
  ),
})
export type FootfallSectionDetails = z.infer<typeof footfallSectionDetailsSchema>

export const footfallSectionSchema = commonTradeAreaSectionSchema.merge(footfallSectionDetailsSchema)
export type FootfallSection = z.infer<typeof footfallSectionSchema>

export type TradeAreaItemWithDistance = TradeAreaItem & {
  distanceMiles: number
}

export type CommonTradeAreaDetailsResponse = {
  nearbyVenues: NearVenue[]
  nearbyLocations: SimpleVenueWithDistance[]
}

export type FootfallTradeAreaDetailsResponse = {
  sections: FootfallSection[]
}

export const matthewsTenantMatchRequestSchema = z.object({
  lat: z.number(),
  lng: z.number(),
  name: z.string(),
})

export type MatthewsTenantMatchRequest = z.infer<typeof matthewsTenantMatchRequestSchema>
